/**
 * @module services/api-upload/patchColumnSortCompareTime
 * @description 对比时间开关与 customizeSetting.column_sort 的 is_compare_date 对齐
 *
 * 与面板 getEffectiveColumnSort 终态一致，底层复用 applyCompareTimeToColumnSort。
 * 仅修正 upload 载荷中的 column_sort，不写回 Context base。
 *
 * 由 applyCustomizeSettingPatches 在 patches.columnSortCompareTime 为 true 时内部调用，
 * 业务侧无需直接 import 本模块。
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { detectCompareTime } from '../../linkage/qbFields/qbLinkage/detectChanges/detectCompareTime';
import { applyCompareTimeToColumnSort } from '../../linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/compareTimeUserFilter';
import { ConfigChartType, type TChartType } from '@dbl/core/chart';

/** patchColumnSortCompareTimeInCustomizeSetting 入参 */
export interface IPatchColumnSortCompareTimeInput {
  /** convert / 前序 patch 产出的 customizeSetting */
  customizeSetting: Record<string, unknown> | undefined;
  /** 当前上传的图表类型，用于定位 ConfigChartType storage 切片 */
  chartType: TChartType;
  /** 当前筛选区（含 compareTimes）；用于 detectCompareTime */
  columnFilters?: unknown[];
  /** QB 维度区；关闭对比时间时还原字段展示名需从此读取 column_display */
  columnDimensions?: unknown[];
}

/**
 * 将 customizeSetting 内当前图表 storage 切片中的 column_sort 按对比时间开关对齐。
 *
 * @returns 有实质变更时返回新对象；无 column_sort / 未变化 / 入参为空时返回原引用
 */
export function patchColumnSortCompareTimeInCustomizeSetting(
  input: IPatchColumnSortCompareTimeInput,
): Record<string, unknown> | undefined {
  const { customizeSetting, chartType, columnFilters, columnDimensions } =
    input;

  if (!customizeSetting) {
    return undefined;
  }

  // customizeSetting 按 chartType 分片存储，如 table / lineChart
  const storageKey = ConfigChartType[chartType];
  if (!storageKey) {
    return customizeSetting;
  }

  const slice = customizeSetting[storageKey];
  if (!slice || typeof slice !== 'object') {
    // 当前图表类型切片不存在或无 column_sort 配置项，无需处理
    return customizeSetting;
  }

  const tableSlice = slice as Record<string, unknown>;
  const rawSort = tableSlice.column_sort;
  if (!Array.isArray(rawSort) || rawSort.length === 0) {
    return customizeSetting;
  }

  // 从即将提交的 filters 推导对比时间开关（与 QB 联动 detectCompareTime 同源）
  const compareTime = detectCompareTime(columnFilters);
  const nextSort = applyCompareTimeToColumnSort(
    rawSort as IColumnSortItem[],
    compareTime,
    columnDimensions ?? [],
    columnFilters,
  );

  // applyCompareTimeToColumnSort 无变更时返回原数组引用，避免无意义的对象拷贝
  if (nextSort === rawSort) {
    return customizeSetting;
  }

  // 仅替换当前 chartType 切片内的 column_sort，其余 storage 与其它字段保持不变
  return {
    ...customizeSetting,
    [storageKey]: {
      ...tableSlice,
      column_sort: nextSort,
    },
  };
}
